
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import GET_MANAGERS from "@/apollo/autocomplete-queries/GetManagers.gql";
import GET_MANAGERS_WITH_PRIVATE_DATA from "@/apollo/autocomplete-queries/GetManagersWithPrivateData.gql";
import DropdownOption from "@/entities/DropdownOption";
import { Manager, Query_RootManagerArgs } from "@/generated/graphql";
import getMultiselectEmployees from "@/helpers/getMultiselectEmployees";
import EmployeeDropdownOption from "@/entities/EmployeeDropdownOption";
import EmployeeMultiselect from "@/components/autocompleteMultiselects/EmployeeMultiselectNew.vue";
import getUserFullData from "@/helpers/getUserFullData";

@Component({
  components: {
    EmployeeMultiselect,
  },
  apollo: {
    managersPublic: {
      query: GET_MANAGERS,
      variables() {
        return this.managerAutocompleteFilter;
      },
      update(data) {
        return data.manager;
      },
      skip() {
        return this.withEmailSearch;
      },
    },
    managersPrivate: {
      query: GET_MANAGERS_WITH_PRIVATE_DATA,
      variables() {
        return this.managerAutocompleteFilter;
      },
      update(data) {
        return data.manager;
      },
      skip() {
        return !this.withEmailSearch;
      },
    },
  },
})
export default class ManagersMultiselect extends Vue {
  @Prop({ default: () => [], type: Array }) value!: string[];
  @Prop({ default: true, type: Boolean }) withBorder!: boolean;
  @Prop({ default: true, type: Boolean }) multiple!: boolean;
  @Prop({ default: "", type: String }) readonly selectLabel!: string;
  @Prop({ type: String, default: "" })
  readonly getParentcontainerStyle?: string;
  @Prop({ default: "", type: String }) readonly deselectLabel!: string;
  @Prop({ default: "Managers", type: String }) readonly placeholder!: string;
  @Prop({ default: false, type: Boolean }) readonly invalid!: boolean;
  @Prop({ type: String }) readonly parentStyles: String;
  @Prop({ type: Number, default: undefined }) readonly max?: number;
  @Prop({ type: Boolean, default: false }) readonly withEmailSearch!: boolean;
  @Prop({ type: Object, default: () => ({}) })
  readonly additionalConditions!: Object;
  @Prop({ type: Boolean, default: false }) disabled!: boolean;
  @Prop({ type: String, default: undefined, required: false })
  readonly cref: string;

  managersPublic: Manager[] = [];
  managersPrivate: Manager[] = [];
  managerSearchString = "";

  get managers(): Manager[] {
    if (this.withEmailSearch) {
      return this.managersPrivate;
    }
    return this.managersPublic;
  }
  @Watch("managers", { immediate: true })
  onManagersChange(newManagers: Manager[]) {
    if (newManagers.length > 0) {
      // Force the dropdown to update when managers data is loaded
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    }
  }

  get isLoading(): boolean {
    const { managersPublic, managersPrivate } = this.$apollo.queries;
    if (this.withEmailSearch) {
      return managersPrivate?.loading;
    }
    return managersPublic?.loading;
  }
  onSetRef(ref) {
    this.$emit("setRef", ref);
  }

  get placeholderText(): string {
    return this.placeholder ? this.placeholder : "Select manager";
  }

  get managersOptions(): any {
    return getMultiselectEmployees({
      employees: this.managers,
      search: this.managerSearchString,
      withEmailSearch: this.withEmailSearch,
    });
  }

  get managerAutocompleteFilter(): Query_RootManagerArgs {
    const conditions: any[] = [this.additionalConditions];
    return {
      where: { _and: conditions },
    };
  }

  get chosenManagers(): EmployeeDropdownOption[] {
    if (!this.managers || this.managers.length === 0) {
      return []; // Return empty array if managers data is not loaded
    }

    const arrayValue = Array.isArray(this.value) ? this.value : [this.value];
    if (!arrayValue.length) {
      return []; // Return empty array if no value is selected
    }

    return this.managers
      .filter((manager) =>
        arrayValue.some((managerId) => manager.id === managerId)
      )
      .map((manager) => {
        const { id, user } = manager;
        const { given_name, family_name, avatar = "" } = user;
        const email = user.private_data?.email;

        return {
          id,
          name: getUserFullData(given_name!, family_name!, email!),
          given_name,
          family_name,
          avatar,
        } as EmployeeDropdownOption;
      });
  }

  handleInput(managers: DropdownOption[]) {
    const managersIds = managers.map((manager) => manager.id);
    this.$emit("input", managersIds);
  }
}
